import { useCallback, useState, useContext } from 'react';
import axios from '@helpers/axios';
import { TeamContext } from '@services/Session/context';
import { TeamGameResponse } from '@dto/api';

export interface TeamIScoreResponse {
  name: string;
  scoreTeamId: string;
}
interface ISCoreStatusResponse {
  iscorePair?: boolean;
  teamId?: string;
  paths?: {
    status?: string;
    sync_games?: string;
    sync_roster?: string;
    unpair?: string;
  };
  naddedGamesOnly?: boolean;
  games?: TeamGameResponse;
  teams?: TeamIScoreResponse[];
  messages?: string[];
  success?: boolean;
}

const useIScore = (teamId: string) => {
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [synced, setSynced] = useState<boolean>(false);
  const [statusData, setStatusData] = useState<ISCoreStatusResponse | null>(
    null
  );
  const [lastModified, setLastModified] = useState<number>(0);
  const teamStore = useContext(TeamContext);

  const checkIScoreStatus = useCallback(async (): Promise<boolean> => {
    setIsSyncing(true);
    setError(null);
    try {
      const response = await axios.get(`/v2/iscore/teams/${teamId}/status`);
      const { iscorePair } = response.data;
      setSynced(iscorePair);
      setStatusData(response.data);
      return iscorePair;
    } catch (err: unknown) {
      setError(err?.message);
      setSynced(false);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [teamId]);

  const syncIScoreSchedule = useCallback(() => {
    setIsSyncing(true);
    axios
      .get(`/v2/iscore/teams/${teamId}/games?added_games=true`)
      .then((response) => {
        const { games } = response.data;
        setLastModified(response.data.teamModifiedTs);
        games.forEach((game: TeamGameResponse) => {
          teamStore.updateSchedule(game);
        });
      })
      .catch((err) => {
        setError(err.message);
        setIsSyncing(false);
      })
      .finally(() => {
        setIsSyncing(false);
      });
  }, [teamId, teamStore]);

  const syncIScoreGames =
    useCallback(async (): Promise<ISCoreStatusResponse> => {
      setIsSyncing(true);
      setSynced(false);
      setError(null);

      try {
        const response = await axios.get(`/v2/iscore/teams/${teamId}/games`);
        const { success, games } = response.data;
        games.forEach((game: TeamGameResponse) => {
          teamStore.updateSchedule(game);
        });
        setSynced(success);
        return response.data;
      } catch (err: unknown) {
        setError(err?.message);
        setSynced(false);
        return { success: false };
      } finally {
        setIsSyncing(false);
      }
    }, [teamId, teamStore]);

  const syncIScoreRoster = useCallback(async (): Promise<boolean> => {
    setIsSyncing(true);
    setError(null);

    try {
      const response = await axios.post(
        `/v2/iscore/teams/${teamId}/sync_roster`
      );
      return response.data.success;
    } catch (err: unknown) {
      setError(err?.message);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [teamId]);

  const unpairFromIScore = useCallback(async (): Promise<boolean> => {
    setIsSyncing(true);
    setError(null);

    try {
      const response = await axios.post(`/v2/iscore/teams/${teamId}/unpair`);
      const { iscorePair } = response.data;
      setSynced(iscorePair);
      return !iscorePair;
    } catch (err: unknown) {
      setError(err?.message);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [teamId]);

  const fetchIscoreTeams = useCallback(
    async (deviceId: string): Promise<ISCoreStatusResponse> => {
      setIsSyncing(true);
      setError(null);

      try {
        const response = await axios.get(`/v2/iscore/devices/${deviceId}`);
        return response.data;
      } catch (err: unknown) {
        setError(err?.message);
        return { success: false };
      } finally {
        setIsSyncing(false);
      }
    },
    []
  );

  const pairWithIscoreTeam = useCallback(
    async (
      deviceId: string,
      linkRequest: { teamId: string; scoreTeamId: string }
    ) => {
      setIsSyncing(true);
      setError(null);

      try {
        const response = await axios.post(
          `/v2/iscore/devices/${deviceId}`,
          linkRequest
        );
        await syncIScoreGames();
        await syncIScoreRoster();
        return response.data;
      } catch (err: unknown) {
        setError(err?.message);
        return null;
      } finally {
        setIsSyncing(false);
      }
    },
    [syncIScoreGames, syncIScoreRoster]
  );

  const iScoreAfterRoster = useCallback(async (): Promise<
    TeamIScoreResponse | false
  > => {
    setIsSyncing(true);
    setError(null);

    try {
      const response = await axios.post(
        `/v2/iscore/teams/${teamId}/after_roster`
      );
      return response;
    } catch (err: unknown) {
      setError(err?.message);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [teamId]);

  return {
    checkIScoreStatus,
    syncIScoreGames,
    syncIScoreSchedule,
    syncIScoreRoster,
    unpairFromIScore,
    fetchIscoreTeams,
    pairWithIscoreTeam,
    iScoreAfterRoster,
    lastModified,
    isSyncing,
    error,
    synced,
    statusData,
  };
};

export default useIScore;
